import React from "react";
import i18n from "i18next";
import styled, { css } from "styled-components";
import { connect, ReactReduxContext } from "react-redux";
import {
  uploadAiImportDocument,
  currentPortfolioSelector,
  getAiImportExtractedData,
  getSortKeyBetween,
  getUuid,
  getTickerUsingId,
  custodianSelector,
  sectionSelector,
  sheetSelector,
  sheetSectionsSelector,
  updateUserPreferences,
  userPreferencesSelector,
  exchangeCodeToNameMap,
  getExchangeRate,
  PVST_VALUE_TICKER_ID,
  aiSupportedFileTypesSelector,
  formatNumberWithCurrency,
  rescanAiImportDocument,
  tickerTypes,
  showToastTip,
  fetchTickerDetails,
  convertPVSTRateToValueExchangeRate,
  getCustodianHistoryFormattedDateString,
  shortenTickerIfNeeded,
  UNKNOWN_TICKER_SHORT_NAME,
  accountLinkingService,
  defaultCustodianTickerSelector,
  toUserTimezoneDate
} from "@kubera/common";
import { withRouter } from "@kubera/common";
import SecondaryButton from "components/button/SecondaryButton";
import PrimaryButton from "components/button/PrimaryButton";
import GridComponentWrapper from "components/grid/GridComponentWrapper";
import {
  GridData,
  GridSheetData,
  GridSectionData,
  GridRowData,
  GridColumnData,
  GridCellData,
  CurrencyCellData,
  cellType
} from "components/grid/GridDataModel";
import { contextMenuItemType } from "components/contextmenu/ContextMenu";
import DownArrow from "assets/images/menu_downarrow.svg?react";
import ToolTip, { toolTipAlignment } from "components/tooltip/ToolTip";
import TickersPickerDialog from "components/planning/variable_dialogs/TickersPickerDialog";
import CustodiansPickerDialog from "components/planning/variable_dialogs/CustodiansPickerDialog";
import { category } from "components/dashboard/DashboardComponentExports";
import {
  itemState,
  getBadgeColorForItemState,
  itemStateBadgeCellWidth,
  itemStateBadgeMarginRight,
  buildAccountLocationMap,
  getAssetDetails,
  getDebtDetails,
  getAccountEntryItemState,
  syncAccountItemsToPortfolio,
  createBulkSyncCollector,
  createUsageStatusCollector,
  flushBulkSync,
  buildNewParentSyncItems,
  getDefaultSectionName,
  buildTargetSectionLocation,
  normalizeAssetForLocation,
  normalizeDebtForLocation,
  getLocationPath,
  resolveExistingHoldingLocation,
  buildParentSectionLocation
} from "./AiImportExports";

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: left;
  flex: 1;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  margin-top: 30px;
`;

const ImportButton = styled(PrimaryButton)`
  min-width: 125px;
  padding-left: 20px;
  padding-right: 20px;
  background-color: ${props => (props.isDisabled && !props.isLoading === true ? "#D2D2D2" : "#029600")};
  color: white;
`;

const RescanHelpText = styled.div`
  display: flex;
  align-items: center;
  width: 60px;
  margin-left: 20px;
  font-weight: 400;
  font-size: 10px;
  line-height: 130%;
  letter-spacing: 0%;
  text-align: right;
  text-transform: uppercase;
  color: #878787;
  white-space: pre-wrap;
`;

const RescanButton = styled(SecondaryButton)`
  margin-left: 6px;
  min-width: 50px;
  padding-left: 20px;
  padding-right: 20px;
`;

const ResultsContainer = styled.div`
  margin-top: 22px;
`;

const GridContainer = styled.div`
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-top: 0;
  margin: ${props => props.margin};
`;

const PartialResultMessage = styled.div`
  width: 910px;
  white-space: pre-line;
  color: #333333;
  padding: 10px 20px 10px 20px;
  font-weight: 400;
  font-size: 12px;
  line-height: 125%;
  letter-spacing: 0%;
  background: #ffe600;
`;

const Grid = styled(GridComponentWrapper)`
  flex: 1;
  margin-left: -1px;
  margin-right: -1px;
  margin-top: -1px;
`;

const CellDropDownContainer = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  margin-left: 4px;
`;

const TickerCellPlaceholder = styled.div`
  opacity: 0.5;
  margin-right: 4px;
  margin-left: -4px;
`;

const BadgeContainer = styled.div`
  display: flex;
  margin-left: -4px;
  margin-right: ${itemStateBadgeMarginRight};
  margin-bottom: 9px;
`;

const BadgeText = styled.div`
  display: flex;
  height: 15px;
  background: ${props => props.bgColor};
  color: ${props =>
    props.itemState === itemState.NEW || props.itemState === itemState.ARCHIVE ? "#FFFFFF" : "#00000099"};
  border: ${props => (props.itemState === itemState.IGNORED ? "1px solid #BCBCBC" : "none")};
  width: 100%;
  align-items: center;
  padding-left: 5px;
  padding-right: 5px;
  border-radius: 7px;
  text-align: right;
  font-weight: 400;
  font-size: 9px;
  line-height: 9px;
  letter-spacing: 0%;
  text-transform: uppercase;
`;

const itemStateSortOrder = {
  [itemState.NEW]: 0,
  [itemState.UPDATE]: 1,
  [itemState.ARCHIVE]: 2,
  [itemState.SAME]: 3,
  [itemState.IGNORED]: 4
};

class AiAccountImportComponent extends React.Component {
  static contextType = ReactReduxContext;

  constructor(props) {
    super(props);

    this.state = {
      dataExtracted: null,
      assetGridData: null,
      debtGridData: null,
      showTickerPickerForAssetRow: null,
      showLocationPickerForAssetRow: null,
      showLocationPickerForDebtRow: null,
      isImportDone: false,
      isImporting: false
    };

    this.locationToolTipRef = React.createRef();

    this.locationMap = {};
    this.defaultLocationMap = {};
    this.ignoredItemIds = [];
    this.archiveItemIds = new Set();
    this.markedAsSameArchiveIds = new Set();
    this.hiddenItemIds = new Set();

    this.handleAssetRowContextMenuSelection = this.handleAssetRowContextMenuSelection.bind(this);
    this.handleDebtRowContextMenuSelection = this.handleDebtRowContextMenuSelection.bind(this);
    this.handleTickerSelection = this.handleTickerSelection.bind(this);
    this.handleTickerPickerDismiss = this.handleTickerPickerDismiss.bind(this);
    this.handleAssetLocationSelection = this.handleAssetLocationSelection.bind(this);
    this.handleDebtLocationSelection = this.handleDebtLocationSelection.bind(this);
    this.handleLocationPickerDismiss = this.handleLocationPickerDismiss.bind(this);
    this.handleImportButtonClick = this.handleImportButtonClick.bind(this);
    this.handleAssetRowUpdate = this.handleAssetRowUpdate.bind(this);
    this.runSync = this.runSync.bind(this);
  }

  componentDidMount() {
    this.handleExtractedDataResponse(this.props.dataExtracted);
    this.props.registerSyncFn?.(this.props.tabKey, this.runSync);
  }

  componentWillUnmount() {
    this.props.registerSyncFn?.(this.props.tabKey, null);
  }

  componentDidUpdate(prevProps, prevState) {
    if (!prevState.assetGridData && !prevState.debtGridData && (this.state.assetGridData || this.state.debtGridData)) {
      this.showLocationToolTipIfNeeded();
    }
  }

  showLocationToolTipIfNeeded() {
    if (this.props.userPreferences?.aiImportLocationTooltipDismissed === true) {
      return;
    }
    const targetId = this.getLocationToolTipTargetId();
    if (!targetId) {
      return;
    }
    setTimeout(() => {
      if (!this.locationToolTipRef.current) return;
      this.locationToolTipRef.current.show(i18n.t("aiImport.locationTooltip"), 0, -10);
    }, 0);
  }

  getLocationToolTipTargetId() {
    const assetRows = this.state.assetGridData?.sheets[0]?.sections[0]?.rows || [];
    if (assetRows.length > 0) {
      return `${GridCellData.getCellId(this.state.assetGridData.id, 0, 0, 0, 2)}`;
    }
    const debtRows = this.state.debtGridData?.sheets[0]?.sections[0]?.rows || [];
    if (debtRows.length > 0) {
      return `${GridCellData.getCellId(this.state.debtGridData.id, 0, 0, 0, 2)}`;
    }
    return null;
  }

  handleLocationToolTipDismiss = () => {
    this.props.updateUserPreferences({ aiImportLocationTooltipDismissed: true });
    this.locationToolTipRef.current?.dismiss();
  };

  getLocationPath(isAsset, location) {
    return getLocationPath(
      this.context.store.getState(),
      this.props.currentPortfolio.id,
      location,
      isAsset ? i18n.t("aiImport.defaultAssetPath") : i18n.t("aiImport.defaultDebtPath")
    );
  }

  getLocationCellDisplay(isAsset, location, itemName) {
    const showsCustodianName = location?.isCustodian === true || location?.isParent === true;
    if (!showsCustodianName) {
      return { value: this.getLocationPath(isAsset, location), description: "" };
    }
    if (location.isParent === true) {
      return { value: itemName, description: this.getLocationPath(isAsset, location) };
    }
    return { value: location.name, description: this.getLocationPath(isAsset, location) };
  }

  handleExtractedDataResponse(dataExtracted) {
    const { locationMap, ignoredItemIds, normalizedAssets, normalizedDebts, hiddenItemIds } =
      this.props.accountLocationResult ||
      buildAccountLocationMap(
        this.context.store.getState(),
        dataExtracted,
        this.props.currentPortfolio,
        this.props.isReviewDataFlow ? null : this.props.defaultTargetSheet,
        this.props.institution,
        this.props.isReviewDataFlow
      );
    this.locationMap = { ...locationMap };
    this.ignoredItemIds = ignoredItemIds;
    this.archiveItemIds = new Set(
      [...normalizedAssets, ...normalizedDebts].filter(item => item.archive === true).map(item => item.id)
    );
    this.markedAsSameArchiveIds = new Set();
    this.hiddenItemIds = hiddenItemIds || new Set();
    this.defaultLocationMap = { ...this.locationMap };
    const updatedDataExtracted = { ...dataExtracted, asset: normalizedAssets, debt: normalizedDebts };
    this.setState({
      dataExtracted: updatedDataExtracted,
      assetGridData: this.getAssetGridData(this.props.currentPortfolio.currency, normalizedAssets),
      debtGridData: this.getDebtGridData(this.props.currentPortfolio.currency, normalizedDebts)
    });
  }

  getRowCountToImport() {
    const isImportable = row =>
      row.cells[0].itemState !== itemState.IGNORED && row.cells[0].itemState !== itemState.SAME;
    return (
      this.state.assetGridData.sheets[0].sections[0].rows.filter(isImportable).length +
      this.state.debtGridData.sheets[0].sections[0].rows.filter(isImportable).length
    );
  }

  handleAssetChange(newGridData) {
    this.setState({ assetGridData: newGridData });
  }

  updateGridRow(gridData, newRow, sheetIndex, sectionIndex, rowIndex) {
    const newGridData = gridData;
    newGridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex] = newRow;
    this.setState({ gridData: newGridData });
  }

  handleAssetRowUpdate(sheetIndex, sectionIndex, rowIndex, updatedRow, isFirstEdit) {
    if (!updatedRow.cells[4].value === false && updatedRow.cells[4].value !== updatedRow.cells[6].value) {
      updatedRow.cells[6].value = updatedRow.cells[4].value;
      this.setState({ gridData: this.state.assetGridData });
    }
  }

  handleCellInvalidTickerAdded(gridData, sheetIndex, sectionIndex, rowIndex, cellIndex) {
    const row = gridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex];
    const cell = row.cells[cellIndex];

    if (!cell.invalidInputText === false && cell.loading === false) {
      let invalidInputText = cell.invalidInputText;
      if (cell.getTickerId(this.context.store.getState()) === PVST_VALUE_TICKER_ID) {
        const rateParsed = cell.rate;
        invalidInputText = rateParsed
          ? getTickerUsingId(this.context.store.getState(), rateParsed.t).shortName
          : cell.currency;
      }
      cell.loading = true;
      this.updateGridRow(gridData, row.clone(), sheetIndex, sectionIndex, rowIndex);
      this.props.fetchTickerDetails(
        invalidInputText,
        toUserTimezoneDate(),
        result => {
          const row = gridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex];
          const cell = row.cells[cellIndex];

          cell.loading = false;
          if (!result === true) {
            this.props.showToastTip("TIP", i18n.t("invalidTickerError"), null, 10000);
            this.updateGridRow(gridData, row.clone(), sheetIndex, sectionIndex, rowIndex);
            return;
          }

          if (cell.getTickerId(this.context.store.getState()) === PVST_VALUE_TICKER_ID) {
            cell.exchangeRateDetails = this.props.convertPVSTRateToValueExchangeRate(
              cell.rate,
              getCustodianHistoryFormattedDateString(toUserTimezoneDate())
            );
            cell.currency = "PVST";
            cell.invalidInputText = null;
          } else {
            cell.exchangeRateDetails = result.exchangeRateDetails;
            cell.currency = result.tickerShortName;
            cell.invalidInputText = null;
          }

          const newRow = row.clone();
          this.updateGridRow(gridData, newRow, sheetIndex, sectionIndex, rowIndex);
        },
        error => {
          const row = gridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex];
          const cell = row.cells[cellIndex];
          cell.loading = false;
          this.updateGridRow(gridData, row.clone(), sheetIndex, sectionIndex, rowIndex);
          this.props.showToastTip("TIP", i18n.t("tickerFetchFailure"), null, 10000);
        }
      );
    }
  }

  populatePreviousValuesForAssetRow(row, asset) {
    const location = this.locationMap[row.id];
    if (location?.isCustodian === true) {
      const extractedAsset = asset || this.state.dataExtracted.asset.find(item => item.id === row.id);
      const custodian = custodianSelector(this.context.store.getState(), location.id);
      const custodianValueTicker = getTickerUsingId(this.context.store.getState(), custodian.valueTickerId);
      const showPreviousValue = !extractedAsset.quantity === true || custodianValueTicker.type === tickerTypes.FIAT;
      if (!custodian === true) {
        return;
      }

      if (showPreviousValue) {
        row.cells[3].previousValue = undefined;
        row.cells[4].previousValue = undefined;
      } else {
        if (custodian.rate && custodian.valueTickerId === PVST_VALUE_TICKER_ID) {
          const rateParsed = JSON.parse(custodian.rate);
          row.cells[3].previousValue = formatNumberWithCurrency(
            this.context.store.getState(),
            rateParsed.p,
            getTickerUsingId(this.context.store.getState(), rateParsed.t).shortName
          );
        } else {
          row.cells[3].previousValue =
            custodian.valueTickerId !== (extractedAsset?.tickerId || extractedAsset?.valueTickerId)
              ? getTickerUsingId(this.context.store.getState(), custodian.valueTickerId).shortName
              : undefined;
        }
        row.cells[4].previousValue = custodian.value;
      }
      row.cells[5].previousValue = custodian.cost;
      row.cells[5].previousCurrency = custodian.costTickerId
        ? getTickerUsingId(this.context.store.getState(), custodian.costTickerId).shortName
        : undefined;

      if (showPreviousValue) {
        row.cells[6].previousValue = custodian.value;
        row.cells[6].previousCurrency = getTickerUsingId(
          this.context.store.getState(),
          custodian.valueTickerId
        ).shortName;
      }
    }
  }

  isArchiveRow(rowId) {
    return this.archiveItemIds.has(rowId) && !this.markedAsSameArchiveIds.has(rowId);
  }

  getItemStateMenuGroup(row) {
    const state = row.cells[0].itemState;
    if (state === itemState.ARCHIVE) {
      return [contextMenuItemType.MARK_AS_SAME];
    }
    if (state === itemState.SAME) {
      return [];
    }
    return [state === itemState.IGNORED ? contextMenuItemType.UNIGNORE : contextMenuItemType.IGNORE];
  }

  setAssetGridRowItemState(row, asset) {
    if (this.markedAsSameArchiveIds.has(row.id)) {
      row.cells[0].itemState = itemState.SAME;
      return;
    }
    const newItemState = getAccountEntryItemState({
      state: this.context.store.getState(),
      location: this.locationMap[row.id],
      isIgnored: this.ignoredItemIds.includes(row.id),
      isArchive: this.isArchiveRow(row.id),
      entryDetails: {
        value: row.cells[6].value,
        currency: row.cells[6].currency,
        rate: row.cells[6].rate,
        cost: row.cells[5].value,
        costCurrency: row.cells[5].currency
      },
      isAsset: true
    });
    row.cells[0].itemState = newItemState;
    if (newItemState === itemState.UPDATE) {
      this.populatePreviousValuesForAssetRow(row, asset);
    }
  }

  getTickerDescription(ticker) {
    if (!ticker) return undefined;
    if (ticker.type === tickerTypes.CRYPTO) return i18n.t("crypto");
    return exchangeCodeToNameMap[ticker.market];
  }

  populateAssetGridRow(row, srcAsset) {
    const assetLocation = this.locationMap[srcAsset.id];
    const asset = normalizeAssetForLocation(this.context.store.getState(), srcAsset, assetLocation);

    row.id = asset.id;
    row.cells[1].value = asset.name;
    {
      const display = this.getLocationCellDisplay(true, this.locationMap[asset.id], asset.name);
      row.cells[2].value = display.value;
      row.cells[2].description = display.description;
    }
    row.cells[3].isEditable = asset.quantity !== undefined && assetLocation?.isCustodian !== true;
    row.cells[3].value =
      asset.quantity && asset.tickerId
        ? shortenTickerIfNeeded(
            this.context.store.getState(),
            getTickerUsingId(this.context.store.getState(), asset.tickerId).shortName
          )
        : undefined;
    row.cells[3].description = asset.tickerId
      ? this.getTickerDescription(getTickerUsingId(this.context.store.getState(), asset.tickerId))
      : undefined;
    row.cells[4].isEditable = asset.quantity !== undefined;
    row.cells[4].value = asset.quantity;
    row.cells[5].value = asset.cost;
    row.cells[5].currency = asset.costTickerId
      ? getTickerUsingId(this.context.store.getState(), asset.costTickerId).shortName
      : undefined;
    row.cells[5].setExchangeRateDetails(
      this.context.store.getState(),
      this.props.currentPortfolio.currency,
      getExchangeRate(this.context.store.getState(), row.cells[5].currency, this.props.currentPortfolio.currency)
    );

    const assetDetails = getAssetDetails(this.context.store.getState(), asset, this.props.currentPortfolio.currency);
    row.cells[6].isEditable = asset.quantity === undefined;
    row.cells[6].value = assetDetails.value;
    row.cells[6].currency = assetDetails.currency;
    row.cells[6].exchangeRateDetails = assetDetails.valueExchangeRate;
    row.cells[6].rate = assetDetails.rate;

    if (assetDetails.rate && assetDetails.valueTickerId === PVST_VALUE_TICKER_ID) {
      const rateParsed = JSON.parse(assetDetails.rate);
      row.cells[6].useRateFromExchangeRateDetails = true;
      row.cells[6].exchangeRateDetails = `{"tickerId":${rateParsed.t},"rate":${rateParsed.p}}`;
      row.cells[3].value = formatNumberWithCurrency(
        this.context.store.getState(),
        rateParsed.p,
        getTickerUsingId(this.context.store.getState(), rateParsed.t).shortName
      );
    }
    this.setAssetGridRowItemState(row, asset);
  }

  sortRowsByInitialItemState(rows) {
    rows.sort((a, b) => {
      const rankA = itemStateSortOrder[a.cells[0].itemState];
      const rankB = itemStateSortOrder[b.cells[0].itemState];
      return (rankA === undefined ? 99 : rankA) - (rankB === undefined ? 99 : rankB);
    });
    let nextSortKey = null;
    for (const row of rows) {
      nextSortKey = getSortKeyBetween(nextSortKey, null);
      row.sortKey = nextSortKey;
    }
  }

  getAssetGridData(currency, assets) {
    var rows = [];
    for (const asset of assets) {
      if (this.hiddenItemIds && this.hiddenItemIds.has(asset.id)) continue;
      const row = this.getEmptyAssetRow(null, asset);
      this.populateAssetGridRow(row, asset);
      rows.push(row);
    }
    this.sortRowsByInitialItemState(rows);

    const section = this.getAssetEmptySection(0, "1");
    section.rows = rows;

    const noQuantityPresent = assets.filter(item => item.quantity === undefined).length === assets.length;
    const noCostPresent = assets.filter(item => item.cost === undefined).length === assets.length;
    if (noQuantityPresent) {
      section.columns[3].hide = true;
      section.columns[4].hide = true;
    }
    if (noCostPresent) {
      section.columns[5].hide = true;
    }

    const sheet = this.getEmptySheet("1");
    sheet.sections = [section];

    const gridData = new GridData(currency, [sheet]);
    gridData.forceShowSheetsTitles = false;
    return gridData;
  }

  getEmptySheet(sortKey) {
    return new GridSheetData(getUuid(), sortKey, null, []);
  }

  getAssetEmptySection(forIndex, sortKey) {
    const badgeColumn = new GridColumnData("", false, false, true);
    badgeColumn.width = itemStateBadgeCellWidth;
    const nameColumn = new GridColumnData(
      i18n.t(this.props.isReviewDataFlow ? "assetOnWebpage" : "assetOnFile"),
      false,
      false,
      false
    );
    const locationColumn = new GridColumnData(i18n.t("portfolioLocation"), true, true, false);
    locationColumn.width = "200px";
    const tickerColumn = new GridColumnData(i18n.t("aiImport.tickerPrice"), true, true, false);
    tickerColumn.width = "60px";
    const quantityColumn = new GridColumnData(i18n.t("qty"), true, true, false);
    quantityColumn.width = "90px";
    const costColumn = new GridColumnData(i18n.t("cost"), true, true, false);
    const valueColumn = new GridColumnData(i18n.t("value"), true, true, false);
    const optionsColumn = new GridColumnData(null, false, false, true);
    const columns = [
      badgeColumn,
      nameColumn,
      locationColumn,
      tickerColumn,
      quantityColumn,
      costColumn,
      valueColumn,
      optionsColumn
    ];

    const sectionData = new GridSectionData(getUuid(), sortKey, "Section " + (forIndex + 1), [], columns, 5, 6, false);
    sectionData.showFooter = false;
    return sectionData;
  }

  getEmptyAssetRow(sortKey, asset) {
    const badgeCell = new GridCellData(cellType.CLICKABLE_TEXT, "", null);
    badgeCell.width = itemStateBadgeCellWidth;
    badgeCell.textAlignment = "right";
    badgeCell.textVerticalAlignment = "top";
    badgeCell.getCellStyles = (rowIndex, cellIndex) => {
      return { marginTop: "3px" };
    };
    badgeCell.getAccessoryView = (sheetIndex, sectionIndex, rowIndex, cellIndex) => {
      const cell = this.state.assetGridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex].cells[cellIndex];
      return (
        <BadgeContainer>
          <BadgeText bgColor={getBadgeColorForItemState(cell.itemState)} itemState={cell.itemState}>
            {cell.itemState}
          </BadgeText>
        </BadgeContainer>
      );
    };
    const nameCell = new GridCellData(
      cellType.TEXT,
      i18n.t(this.props.isReviewDataFlow ? "assetOnWebpage" : "assetOnFile"),
      null
    );
    nameCell.textAlignment = "left";
    nameCell.textVerticalAlignment = "top";

    const locationCell = new GridCellData(cellType.CLICKABLE_TEXT, i18n.t("portfolioLocation"), null);
    locationCell.width = "200px";
    locationCell.textAlignment = "left";
    locationCell.textVerticalAlignment = "top";
    locationCell.multiLine = true;
    locationCell.onClick = (e, sheetIndex, sectionIndex, rowIndex, cellIndex) => {
      this.handleLocationToolTipDismiss();
      this.setState({
        showLocationPickerForAssetRow: this.state.assetGridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex]
      });
    };

    const tickerCell = new GridCellData(cellType.CLICKABLE_TEXT, i18n.t("aiImport.tickerPrice"), null);
    tickerCell.textAlignment = "left";
    tickerCell.textVerticalAlignment = "top";
    tickerCell.width = "60px";
    tickerCell.getAccessoryView = (sheetIndex, sectionIndex, rowIndex, cellIndex) => {
      const row = this.state.assetGridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex];
      const cell = row.cells[cellIndex];
      if (this.locationMap[row.id]?.isCustodian === true) {
        return null;
      }
      return (
        <CellDropDownContainer>
          {cell.value ? null : (
            <TickerCellPlaceholder>{cell.isEditable === true ? i18n.t("select") : "---"}</TickerCellPlaceholder>
          )}
          {!cell.value === true && cell.isEditable === true && <DownArrow />}
        </CellDropDownContainer>
      );
    };
    tickerCell.onClick = (e, sheetIndex, sectionIndex, rowIndex, cellIndex) => {
      const row = this.state.assetGridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex];
      if (this.locationMap[row.id]?.isCustodian === true) {
        return;
      }
      this.setState({
        showTickerPickerForAssetRow: row
      });
    };
    const quantityCell = new GridCellData(cellType.NUMBER, i18n.t("qty"), null);
    quantityCell.textVerticalAlignment = "top";
    quantityCell.width = "90px";
    quantityCell.emptyValueIndicator = "---";
    const costCell = new CurrencyCellData(
      cellType.CURRENCY,
      i18n.t("cost"),
      null,
      this.props.currentPortfolio.currency
    );
    costCell.textVerticalAlignment = "top";
    costCell.emptyValueIndicator = "---";
    costCell.getCellStyles = (rowIndex, cellIndex) => {
      return { marginTop: "-2px", minHeight: "17px" };
    };
    const valueCell = new CurrencyCellData(
      cellType.CURRENCY,
      i18n.t("value"),
      null,
      this.props.currentPortfolio.currency
    );
    valueCell.allowPVST = true;
    valueCell.textVerticalAlignment = "top";
    valueCell.getCellStyles = (rowIndex, cellIndex) => {
      return { marginTop: "-2px" };
    };
    const optionsCell = new GridCellData(cellType.OPTIONS, "", null);
    const cells = [badgeCell, nameCell, locationCell, tickerCell, quantityCell, costCell, valueCell, optionsCell];

    const rowData = new GridRowData(getUuid(), sortKey, "entry-id-" + Math.random(), cells, 1, false, () => {
      return true;
    });
    rowData.showHint = false;
    rowData.getContextMenuItems = (row, rowIndex) => {
      const disableClearTicker = row.cells[4].value === undefined || row.cells[3].value === undefined;
      const disableCost = row.cells[5].value === undefined;
      const disableCopy =
        this.isCopyLocationDisabled(row.id) ||
        rowIndex === this.state.assetGridData.sheets[0].sections[0].rows.length - 1;
      return [
        [
          { ...contextMenuItemType.CLEAR_TICKER, disabled: disableClearTicker },
          { ...contextMenuItemType.CLEAR_COST, disabled: disableCost },
          { ...contextMenuItemType.COPY_LOCATION_BELOW, disabled: disableCopy }
        ],
        [contextMenuItemType.RESET_CHANGES],
        this.getItemStateMenuGroup(row)
      ].filter(group => group.length > 0);
    };
    return rowData;
  }

  handleDebtChange(newGridData) {
    this.setState({ debtGridData: newGridData });
  }

  populatePreviousValuesForDebtRow(row) {
    const location = this.locationMap[row.id];
    if (location?.isCustodian === true) {
      const custodian = custodianSelector(this.context.store.getState(), location.id);
      if (!custodian === true) {
        return;
      }
      row.cells[3].previousValue = custodian.value;
      row.cells[3].previousCurrency = getTickerUsingId(
        this.context.store.getState(),
        custodian.valueTickerId
      ).shortName;
    }
  }

  setDebtGridRowItemState(row) {
    if (this.markedAsSameArchiveIds.has(row.id)) {
      row.cells[0].itemState = itemState.SAME;
      return;
    }
    const newItemState = getAccountEntryItemState({
      state: this.context.store.getState(),
      location: this.locationMap[row.id],
      isIgnored: this.ignoredItemIds.includes(row.id),
      isArchive: this.isArchiveRow(row.id),
      entryDetails: {
        value: row.cells[3].value,
        currency: row.cells[3].currency
      },
      isAsset: false
    });
    row.cells[0].itemState = newItemState;
    if (newItemState === itemState.UPDATE) {
      this.populatePreviousValuesForDebtRow(row);
    }
  }

  populateDebtGridRow(row, srcDebt) {
    const debtLocation = this.locationMap[srcDebt.id];
    const debt = normalizeDebtForLocation(srcDebt, debtLocation);

    row.id = debt.id;
    row.cells[1].value = debt.name;
    {
      const display = this.getLocationCellDisplay(false, this.locationMap[debt.id], debt.name);
      row.cells[2].value = display.value;
      row.cells[2].description = display.description;
    }

    const debtDetails = getDebtDetails(this.context.store.getState(), debt, this.props.currentPortfolio.currency);
    row.cells[3].value = debtDetails.value;
    row.cells[3].currency = debtDetails.currency;
    this.setDebtGridRowItemState(row);
  }

  getDebtGridData(currency, debts) {
    var rows = [];
    for (const debt of debts) {
      if (this.hiddenItemIds && this.hiddenItemIds.has(debt.id)) continue;
      const row = this.getEmptyDebtRow(null, debt);
      this.populateDebtGridRow(row, debt);
      rows.push(row);
    }
    this.sortRowsByInitialItemState(rows);

    const section = this.getDebtEmptySection(0, "1");
    section.rows = rows;

    const sheet = this.getEmptySheet("1");
    sheet.sections = [section];

    const gridData = new GridData(currency, [sheet]);
    gridData.forceShowSheetsTitles = false;
    return gridData;
  }

  getDebtEmptySection(forIndex, sortKey) {
    const badgeColumn = new GridColumnData("", false, false, true);
    badgeColumn.width = itemStateBadgeCellWidth;
    const nameColumn = new GridColumnData(
      i18n.t(this.props.isReviewDataFlow ? "debtOnWebpage" : "debtOnFile"),
      false,
      false,
      false
    );
    const locationColumn = new GridColumnData(i18n.t("portfolioLocation"), true, true, false);
    locationColumn.width = "200px";
    const valueColumn = new GridColumnData(i18n.t("balance"), true, true, false);
    const optionsColumn = new GridColumnData(null, false, false, true);
    const columns = [badgeColumn, nameColumn, locationColumn, valueColumn, optionsColumn];

    const sectionData = new GridSectionData(getUuid(), sortKey, "Section " + (forIndex + 1), [], columns, 3, 3, false);
    sectionData.showFooter = false;
    return sectionData;
  }

  getEmptyDebtRow(sortKey, debt) {
    const badgeCell = new GridCellData(cellType.CLICKABLE_TEXT, "", null);
    badgeCell.width = itemStateBadgeCellWidth;
    badgeCell.textAlignment = "right";
    badgeCell.textVerticalAlignment = "top";
    badgeCell.getCellStyles = (rowIndex, cellIndex) => {
      return { marginTop: "3px" };
    };
    badgeCell.getAccessoryView = (sheetIndex, sectionIndex, rowIndex, cellIndex) => {
      const cell = this.state.debtGridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex].cells[cellIndex];
      return (
        <BadgeContainer>
          <BadgeText bgColor={getBadgeColorForItemState(cell.itemState)} itemState={cell.itemState}>
            {cell.itemState}
          </BadgeText>
        </BadgeContainer>
      );
    };
    const nameCell = new GridCellData(
      cellType.TEXT,
      i18n.t(this.props.isReviewDataFlow ? "debtOnWebpage" : "debtOnFile"),
      null
    );
    nameCell.textAlignment = "left";
    nameCell.textVerticalAlignment = "top";

    const locationCell = new GridCellData(cellType.CLICKABLE_TEXT, i18n.t("portfolioLocation"), null);
    locationCell.textAlignment = "left";
    locationCell.textVerticalAlignment = "top";
    locationCell.width = "200px";
    locationCell.multiLine = true;
    locationCell.onClick = (e, sheetIndex, sectionIndex, rowIndex, cellIndex) => {
      this.handleLocationToolTipDismiss();
      this.setState({
        showLocationPickerForDebtRow: this.state.debtGridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex]
      });
    };

    const valueCell = new CurrencyCellData(
      cellType.CURRENCY,
      i18n.t("balance"),
      null,
      this.props.currentPortfolio.currency
    );
    const optionsCell = new GridCellData(cellType.OPTIONS, "", null);
    const cells = [badgeCell, nameCell, locationCell, valueCell, optionsCell];

    const rowData = new GridRowData(getUuid(), sortKey, "entry-id-" + Math.random(), cells, 1, false, () => {
      return true;
    });
    rowData.showHint = false;
    rowData.getContextMenuItems = (row, rowIndex) => {
      const disableCopy =
        this.isCopyLocationDisabled(row.id) ||
        rowIndex === this.state.debtGridData.sheets[0].sections[0].rows.length - 1;
      return [
        [{ ...contextMenuItemType.COPY_LOCATION_BELOW, disabled: disableCopy }],
        [contextMenuItemType.RESET_CHANGES],
        this.getItemStateMenuGroup(row)
      ].filter(group => group.length > 0);
    };
    return rowData;
  }

  handleAssetRowContextMenuSelection(sheetIndex, sectionIndex, rowIndex, row, menuItem) {
    if (menuItem.id === contextMenuItemType.CLEAR_TICKER.id) {
      const newGridData = this.state.assetGridData;
      newGridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex] = row;
      const asset = this.state.dataExtracted.asset.find(item => item.id === row.id);
      row.cells[6].isEditable = true;
      row.cells[3].value = undefined;
      row.cells[3].description = undefined;
      row.cells[4].isEditable = false;
      row.cells[4].value = undefined;
      if (asset?.value !== undefined && asset.valueTickerId) {
        row.cells[6].value = asset.value;
        row.cells[6].currency = getTickerUsingId(this.context.store.getState(), asset.valueTickerId).shortName;
        row.cells[6].setExchangeRateDetails(
          this.context.store.getState(),
          this.props.currentPortfolio.currency,
          getExchangeRate(this.context.store.getState(), row.cells[6].currency, this.props.currentPortfolio.currency)
        );
      } else {
        row.cells[6].currency = defaultCustodianTickerSelector(
          this.context.store.getState(),
          this.props.currentPortfolio.currency
        ).shortName;
      }
      this.setState({ assetGridData: newGridData });
    } else if (menuItem.id === contextMenuItemType.CLEAR_COST.id) {
      const newGridData = this.state.assetGridData;
      newGridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex] = row;
      row.cells[5].isEditable = false;
      row.cells[5].currency = undefined;
      row.cells[5].value = undefined;
      this.setAssetGridRowItemState(row);
      this.setState({ assetGridData: newGridData });
    } else if (menuItem.id === contextMenuItemType.RESET_CHANGES.id) {
      const newGridData = this.state.assetGridData;
      newGridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex] = row;
      const asset = this.state.dataExtracted.asset.find(item => item.id === row.id);
      this.locationMap[row.id] = this.defaultLocationMap[row.id];
      this.markedAsSameArchiveIds.delete(row.id);
      for (const cell of row.cells) {
        cell.previousValue = undefined;
        cell.previousCurrency = undefined;
      }
      this.populateAssetGridRow(row, asset);
      this.setState({ assetGridData: newGridData });
      this.publishAccountLocations();
    } else if (menuItem.id === contextMenuItemType.IGNORE.id) {
      const newGridData = this.state.assetGridData;
      newGridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex] = row;
      row.cells[0].itemState = itemState.IGNORED;
      this.ignoredItemIds.push(row.id);
      this.setState({ assetGridData: newGridData });
      this.props.onPendingItemsChange(this.props.tabKey, this.ignoredItemIds.length);
    } else if (menuItem.id === contextMenuItemType.UNIGNORE.id) {
      const newGridData = this.state.assetGridData;
      newGridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex] = row;
      this.ignoredItemIds = this.ignoredItemIds.filter(item => item !== row.id);
      this.setAssetGridRowItemState(row);
      this.setState({ assetGridData: newGridData });
      this.props.onPendingItemsChange(this.props.tabKey, this.ignoredItemIds.length);
    } else if (menuItem.id === contextMenuItemType.MARK_AS_SAME.id) {
      const newGridData = this.state.assetGridData;
      newGridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex] = row;
      this.markedAsSameArchiveIds.add(row.id);
      this.setAssetGridRowItemState(row);
      this.setState({ assetGridData: newGridData });
    } else if (menuItem.id === contextMenuItemType.COPY_LOCATION_BELOW.id) {
      const currentRowLocation = this.getCopyableLocation(row.id);
      const newGridData = this.state.assetGridData;
      const rowsBelow = newGridData.sheets[sheetIndex].sections[sectionIndex].rows.slice(rowIndex + 1);
      for (const row of rowsBelow) {
        this.locationMap[row.id] = this.resolveRowLocation(row.id, true, {
          ...currentRowLocation,
          newCustodianId: undefined
        });
        for (const cell of row.cells) {
          cell.previousValue = undefined;
          cell.previousCurrency = undefined;
        }

        this.populateAssetGridRow(row, this.state.dataExtracted.asset.find(item => item.id === row.id));
      }
      this.setState({ assetGridData: newGridData });
      this.publishAccountLocations();
    }
  }

  handleTickerPickerDismiss() {
    this.setState({ showTickerPickerForAssetRow: null });
  }

  handleTickerSelection(value) {
    const row = this.state.showTickerPickerForAssetRow;
    const ticker = getTickerUsingId(this.context.store.getState(), value.items[0].id);
    row.cells[3].value = shortenTickerIfNeeded(this.context.store.getState(), ticker.shortName);
    row.cells[3].description = this.getTickerDescription(ticker);
    row.cells[6].currency = ticker.shortName;
    this.setState({ showTickerPickerForAssetRow: null });
  }

  handleLocationPickerDismiss() {
    this.setState({ showLocationPickerForAssetRow: null, showLocationPickerForDebtRow: null });
  }

  handleAssetLocationSelection(row, value) {
    if (value.category === category.DEBT || value.sheet?.category === category.DEBT) {
      const newDataExtracted = this.state.dataExtracted;
      const debt = newDataExtracted.asset.find(item => item.id === row.id);
      newDataExtracted.asset = newDataExtracted.asset.filter(item => item.id !== row.id);
      newDataExtracted.debt.push(debt);

      const newDebtGridData = this.state.debtGridData;
      const debtRows = newDebtGridData.sheets[0].sections[0].rows;
      const sortKey = getSortKeyBetween(debtRows[debtRows.length - 1]?.sortKey, null);
      const debtRow = this.getEmptyDebtRow(sortKey, debt);
      this.populateDebtGridRow(debtRow, debt);
      debtRows.push(debtRow);

      const newAssetGridData = this.state.assetGridData;
      newAssetGridData.sheets[0].sections[0].rows = newAssetGridData.sheets[0].sections[0].rows.filter(
        item => item.id !== row.id
      );

      this.setState({
        dataExtracted: newDataExtracted,
        debtGridData: newDebtGridData,
        assetGridData: newAssetGridData
      });

      this.handleDebtLocationSelection(debtRow, value);
      return;
    }

    for (const cell of row.cells) {
      cell.previousValue = undefined;
      cell.previousCurrency = undefined;
    }

    if (value.isSheet) {
      const sectionLocation = buildTargetSectionLocation(
        this.context.store.getState(),
        this.props.currentPortfolio.id,
        value,
        this.props.institution || getDefaultSectionName(this.props.isReviewDataFlow)
      );
      const existingLocationBasedOnPath = this.findLocationBasedOnPath(true, sectionLocation);
      if (!existingLocationBasedOnPath === true) {
        value = sectionLocation;
      }
    }
    if (value.newParentCustodianId !== undefined) {
      value = { ...value, newParentCustodianId: undefined };
    }
    if (value.isNewAccountRequest === true) {
      value = this.createInlineAccountLocation(row.id, value.parentCustodianName);
    }
    this.locationMap[row.id] = this.resolveRowLocation(row.id, true, value);

    const asset = this.state.dataExtracted.asset.find(item => item.id === row.id);
    const display = this.getLocationCellDisplay(true, this.locationMap[row.id], asset?.name);
    row.cells[1].value = asset?.name;
    row.cells[2].value = display.value;
    row.cells[2].description = display.description;

    this.populateAssetGridRow(row, asset);
    this.setState({ assetGridData: this.state.assetGridData });
    this.publishAccountLocations();
  }

  handleDebtRowContextMenuSelection(sheetIndex, sectionIndex, rowIndex, row, menuItem) {
    if (menuItem.id === contextMenuItemType.IGNORE.id) {
      const newGridData = this.state.debtGridData;
      newGridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex] = row;
      row.cells[0].itemState = itemState.IGNORED;
      this.ignoredItemIds.push(row.id);
      this.setState({ debtGridData: newGridData });
      this.props.onPendingItemsChange(this.props.tabKey, this.ignoredItemIds.length);
    } else if (menuItem.id === contextMenuItemType.UNIGNORE.id) {
      const newGridData = this.state.debtGridData;
      newGridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex] = row;
      this.ignoredItemIds = this.ignoredItemIds.filter(item => item !== row.id);
      this.setDebtGridRowItemState(row);
      this.setState({ debtGridData: newGridData });
      this.props.onPendingItemsChange(this.props.tabKey, this.ignoredItemIds.length);
    } else if (menuItem.id === contextMenuItemType.MARK_AS_SAME.id) {
      const newGridData = this.state.debtGridData;
      newGridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex] = row;
      this.markedAsSameArchiveIds.add(row.id);
      this.setDebtGridRowItemState(row);
      this.setState({ debtGridData: newGridData });
    } else if (menuItem.id === contextMenuItemType.COPY_LOCATION_BELOW.id) {
      const currentRowLocation = this.getCopyableLocation(row.id);
      const newGridData = this.state.debtGridData;
      const rowsBelow = newGridData.sheets[sheetIndex].sections[sectionIndex].rows.slice(rowIndex + 1);
      for (const row of rowsBelow) {
        this.locationMap[row.id] = this.resolveRowLocation(row.id, false, {
          ...currentRowLocation,
          newCustodianId: undefined
        });

        for (const cell of row.cells) {
          cell.previousValue = undefined;
          cell.previousCurrency = undefined;
        }

        this.populateDebtGridRow(row, this.state.dataExtracted.debt.find(item => item.id === row.id));
      }
      this.setState({ debtGridData: newGridData });
      this.publishAccountLocations();
    } else if (menuItem.id === contextMenuItemType.RESET_CHANGES.id) {
      const newGridData = this.state.debtGridData;
      newGridData.sheets[sheetIndex].sections[sectionIndex].rows[rowIndex] = row;
      const debt = this.state.dataExtracted.debt.find(item => item.id === row.id);
      this.locationMap[row.id] = this.defaultLocationMap[row.id];
      this.markedAsSameArchiveIds.delete(row.id);
      for (const cell of row.cells) {
        cell.previousValue = undefined;
        cell.previousCurrency = undefined;
      }
      this.populateDebtGridRow(row, debt);
      this.setState({ debtGridData: newGridData });
      this.publishAccountLocations();
    }
  }

  findLocationBasedOnPath(isAsset, location) {
    const locations = Object.values(this.locationMap);
    return locations.find(item => this.getLocationPath(isAsset, item) === this.getLocationPath(isAsset, location));
  }

  // Accounts created in this session need a stable client-side custodian id so cash flows /
  // fund schedules in the other tabs can attach to the same custodian. Carry the existing id over
  // when the user re-targets a row to another new account, and mint one if it doesn't have any yet.
  withStableNewCustodianId(rowId, value) {
    if (value.isCustodian === true || value.newParentCustodianId !== undefined) {
      return value;
    }
    return {
      ...value,
      newCustodianId: value.newCustodianId || this.locationMap[rowId]?.newCustodianId || getUuid()
    };
  }

  // A row re-targeted to an existing account resolves to that account's matching entry, so the
  // account the user actually picked is what gets copied down, not the entry it happened to land on.
  getCopyableLocation(rowId) {
    const location = this.locationMap[rowId];
    return location?.chosenParentLocation || location;
  }

  isCopyLocationDisabled(rowId) {
    return this.getCopyableLocation(rowId)?.isCustodian === true;
  }

  getClaimedCustodianIds(exceptRowId) {
    const claimed = new Set();
    for (const [rowId, location] of Object.entries(this.locationMap)) {
      if (rowId !== exceptRowId && location?.isCustodian === true && location.id) {
        claimed.add(location.id);
      }
    }
    return claimed;
  }

  getRowItem(isAsset, rowId) {
    const items = isAsset ? this.state.dataExtracted.asset : this.state.dataExtracted.debt;
    return items.find(item => item.id === rowId);
  }

  resolveRowLocation(rowId, isAsset, value) {
    if (this.archiveItemIds.has(rowId)) {
      return this.withStableNewCustodianId(rowId, value);
    }
    const state = this.context.store.getState();
    const item = this.getRowItem(isAsset, rowId);
    const tickerId =
      isAsset && item ? getAssetDetails(state, item, this.props.currentPortfolio.currency)?.valueTickerId : undefined;
    const existingHolding = resolveExistingHoldingLocation(
      state,
      this.props.currentPortfolio.id,
      value,
      item?.name,
      tickerId,
      this.getClaimedCustodianIds(rowId)
    );
    return existingHolding || this.withStableNewCustodianId(rowId, value);
  }

  getTargetSectionLocationForRow(rowId) {
    const state = this.context.store.getState();
    const location = this.locationMap[rowId];
    const sectionLocation = buildParentSectionLocation(
      state,
      this.props.currentPortfolio.id,
      location?.chosenParentLocation || location
    );
    if (sectionLocation) {
      return sectionLocation;
    }
    const sheet =
      this.props.defaultTargetSheet?.category === category.ASSET
        ? this.props.defaultTargetSheet
        : this.props.currentPortfolio.details.sheet.filter(item => item.category === category.ASSET)[0];
    return buildTargetSectionLocation(
      state,
      this.props.currentPortfolio.id,
      sheet,
      this.props.institution || getDefaultSectionName(this.props.isReviewDataFlow)
    );
  }

  // Mirrors the wiring buildAccountLocationMap sets up for a scanned new parent: a hidden parent item
  // whose location carries newParentCustodianId, which is what makes the account get created on sync.
  createInlineAccountLocation(rowId, accountName) {
    const state = this.context.store.getState();
    const portfolioCurrency = this.props.currentPortfolio.currency;
    const sectionLocation = this.getTargetSectionLocationForRow(rowId);
    const item = this.state.dataExtracted.asset.find(entry => entry.id === rowId);
    const defaultTicker = defaultCustodianTickerSelector(state, portfolioCurrency);
    const details = item ? getAssetDetails(state, item, defaultTicker.shortName) : null;
    const exchangeRate = details ? getExchangeRate(state, details.currency, defaultTicker.shortName) || 1 : 1;

    const parentItem = {
      id: getUuid(),
      name: accountName,
      value: details?.value === undefined ? 0 : Number(details.value) * exchangeRate,
      valueTickerId: defaultTicker.id,
      isSyntheticParent: true,
      isNamedByUser: true
    };
    const parentCustodianId = getUuid();
    this.state.dataExtracted.asset.push(parentItem);
    this.hiddenItemIds.add(parentItem.id);
    this.locationMap[parentItem.id] = { ...sectionLocation, newParentCustodianId: parentCustodianId };

    return {
      isParent: true,
      isNewParent: true,
      createNewSection: sectionLocation.createNewSection === true,
      reuseEmptySection: sectionLocation.reuseEmptySection === true,
      parentCustodianId,
      parentCustodianName: accountName,
      section: sectionLocation.section,
      sheet: sectionLocation.sheet
    };
  }

  publishAccountLocations() {
    this.props.onAccountLocationsChange?.({ ...this.locationMap });
  }

  handleDebtLocationSelection(row, value) {
    if (value.category === category.ASSET || value.sheet?.category === category.ASSET) {
      const newDataExtracted = this.state.dataExtracted;
      const asset = newDataExtracted.debt.find(item => item.id === row.id);
      newDataExtracted.debt = newDataExtracted.debt.filter(item => item.id !== row.id);
      newDataExtracted.asset.push(asset);

      const newAssetGridData = this.state.assetGridData;
      const assetRows = newAssetGridData.sheets[0].sections[0].rows;
      const sortKey = getSortKeyBetween(assetRows[assetRows.length - 1]?.sortKey, null);
      const assetRow = this.getEmptyAssetRow(sortKey, asset);
      this.populateAssetGridRow(assetRow, asset);
      assetRows.push(assetRow);

      const newDebtGridData = this.state.debtGridData;
      newDebtGridData.sheets[0].sections[0].rows = newDebtGridData.sheets[0].sections[0].rows.filter(
        item => item.id !== row.id
      );

      this.setState({
        dataExtracted: newDataExtracted,
        debtGridData: newDebtGridData,
        assetGridData: newAssetGridData
      });

      this.handleAssetLocationSelection(assetRow, value);
      return;
    }

    for (const cell of row.cells) {
      cell.previousValue = undefined;
      cell.previousCurrency = undefined;
    }
    if (value.isSheet) {
      const sectionLocation = buildTargetSectionLocation(
        this.context.store.getState(),
        this.props.currentPortfolio.id,
        value,
        this.props.institution || getDefaultSectionName(this.props.isReviewDataFlow)
      );
      const existingLocationBasedOnPath = this.findLocationBasedOnPath(false, sectionLocation);
      if (!existingLocationBasedOnPath === true) {
        value = sectionLocation;
      }
    }
    if (value.newParentCustodianId !== undefined) {
      value = { ...value, newParentCustodianId: undefined };
    }
    this.locationMap[row.id] = this.resolveRowLocation(row.id, false, value);

    const debtName = this.state.dataExtracted.debt.find(item => item.id === row.id)?.name;
    const display = this.getLocationCellDisplay(false, this.locationMap[row.id], debtName);
    row.cells[1].value = debtName;
    row.cells[2].value = display.value;
    row.cells[2].description = display.description;

    this.setDebtGridRowItemState(row);
    this.setState({ debtGridData: this.state.debtGridData });
    this.publishAccountLocations();
  }

  // Enqueue all syncable accounts into the shared bulk collector (no flush). Used both by this tab's
  // own Import button and by the parent's combined Assets + Cash flow sync. Returns the set of
  // custodian ids being created so cash flow sync can fold their names into the create lastLink.
  handleImportButtonClick(e) {
    this.runSync();
  }

  // Sync this tab's accounts in their own bulk submit. options.onSynced fires after the flush
  // completes (custodians created server-side) — the parent uses it to sequence the cash flow /
  // fund schedule tabs after accounts exist during a combined Assets + Cash flow sync.
  runSync(options = {}) {
    const assetRows = this.state.assetGridData.sheets[0].sections[0].rows.filter(
      item =>
        !this.locationMap[item.id] === false &&
        item.cells[6].value !== undefined &&
        item.cells[6].currency !== UNKNOWN_TICKER_SHORT_NAME
    );
    const debtRows = this.state.debtGridData.sheets[0].sections[0].rows.filter(
      item =>
        !this.locationMap[item.id] === false &&
        item.cells[3].value !== undefined &&
        item.cells[3].currency !== UNKNOWN_TICKER_SHORT_NAME
    );

    const assetItems = assetRows.map(row => ({
      id: row.id,
      name: row.cells[1].value,
      extractedData: this.state.dataExtracted.asset.find(item => item.id === row.id),
      itemState: row.cells[0].itemState,
      isAsset: true,
      details: {
        value: row.cells[6].value,
        currency: row.cells[6].currency,
        valueExchangeRate: row.cells[6].exchangeRateDetails,
        rate: row.cells[6].rate,
        cost: row.cells[5].value,
        costCurrency: row.cells[5].currency,
        costExchangeRate: row.cells[5].exchangeRateDetails
      }
    }));
    const debtItems = debtRows.map(row => ({
      id: row.id,
      name: row.cells[1].value,
      extractedData: this.state.dataExtracted.debt.find(item => item.id === row.id),
      itemState: row.cells[0].itemState,
      isAsset: false,
      details: {
        value: row.cells[3].value,
        currency: row.cells[3].currency,
        valueExchangeRate: row.cells[3].exchangeRateDetails
      }
    }));

    const referencedParentCustodianIds = new Set();
    for (const item of [...assetItems, ...debtItems]) {
      const loc = this.locationMap[item.id];
      if (loc?.isParent === true && loc.parentCustodianId) {
        referencedParentCustodianIds.add(loc.parentCustodianId);
      }
    }
    const newParentItems = buildNewParentSyncItems(
      this.context.store.getState(),
      this.state.dataExtracted,
      this.locationMap,
      this.props.currentPortfolio
    ).filter(p => referencedParentCustodianIds.has(this.locationMap[p.id]?.newParentCustodianId));

    const items = [...newParentItems, ...assetItems, ...debtItems];

    this.setState({ isImporting: true });
    this.props.onImportingStatusChange(true);

    const bulkCollector = createBulkSyncCollector();
    const usageStatusCollector = createUsageStatusCollector();
    syncAccountItemsToPortfolio({
      dispatch: this.context.store.dispatch,
      state: this.context.store.getState(),
      portfolio: this.props.currentPortfolio,
      documentId: this.props.documentId,
      items,
      locationMap: this.locationMap,
      lastLinkMetadata: this.props.lastLinkMetadata,
      usageStatusCollector,
      bulkCollector
    });
    flushBulkSync({
      dispatch: this.context.store.dispatch,
      portfolio: this.props.currentPortfolio,
      documentId: this.props.documentId,
      bulkCollector,
      usageStatusCollector,
      onComplete: result => {
        const newAssetGridData = this.state.assetGridData;
        const newDebtGridData = this.state.debtGridData;
        newAssetGridData.isEditable = false;
        for (const row of newAssetGridData.sheets[0].sections[0].rows) {
          row.cells[7].hide = true;
        }
        for (const row of newDebtGridData.sheets[0].sections[0].rows) {
          row.cells[4].hide = true;
        }
        newDebtGridData.isEditable = false;
        this.setState({
          isImportDone: true,
          isImporting: false,
          assetGridData: newAssetGridData,
          debtGridData: newDebtGridData
        });
        this.props.onPendingItemsChange(
          this.props.tabKey,
          this.ignoredItemIds.length + this.getRowCountToImport(),
          true
        );
        this.props.onImportingStatusChange(false);
        this.publishAccountLocations();
        options.onSynced?.(result);
      },
      onError: () => {
        this.setState({ isImporting: false });
        this.props.onImportingStatusChange(false);
        options.onError?.();
      }
    });
  }

  getNewPathSuggestions() {
    var suggestions = {};
    var newLocations = [];
    const seenSectionIds = new Set();
    const seenParentCustodianIds = new Set();
    for (const location of Object.values(this.locationMap)) {
      if (location.isParent === true && location.isNewParent === true) {
        if (seenParentCustodianIds.has(location.parentCustodianId) === false) {
          seenParentCustodianIds.add(location.parentCustodianId);
          newLocations.push(location);
        }
      } else if (location.createNewSection === true && location.section) {
        if (seenSectionIds.has(location.section.id) === false) {
          seenSectionIds.add(location.section.id);
          newLocations.push(location);
        }
      }
    }
    suggestions[category.ASSET] = newLocations.filter(item => item.sheet?.category === category.ASSET);
    suggestions[category.DEBT] = newLocations.filter(item => item.sheet?.category === category.DEBT);
    return suggestions;
  }

  isRowHolding(row) {
    const location = this.locationMap[row.id];
    if (location?.isParent === true || !!location?.chosenParentLocation) {
      return true;
    }
    const item = this.state.dataExtracted.asset.find(asset => asset.id === row.id);
    return !!item?.parentAccount;
  }

  getHoldingAccountSuggestions(row) {
    const state = this.context.store.getState();
    const suggestions = [];
    const seenParentIds = new Set();

    const addParentSuggestion = location => {
      if (location?.isParent !== true || seenParentIds.has(location.parentCustodianId)) {
        return;
      }
      seenParentIds.add(location.parentCustodianId);
      suggestions.push({
        name: location.parentCustodianName,
        isNew: location.isNewParent === true,
        location
      });
    };

    const currentLocation = this.locationMap[row.id];
    addParentSuggestion(this.defaultLocationMap[row.id]);
    addParentSuggestion(currentLocation?.chosenParentLocation || currentLocation);
    for (const location of Object.values(this.locationMap)) {
      if (location?.isNewParent === true && location.sheet?.category === category.ASSET) {
        addParentSuggestion(location);
      }
    }

    for (const custodian of this.props.currentPortfolio.details.custodian) {
      if (custodian.linkType !== accountLinkingService.MANUAL_CONNECTED || custodian.parentId) {
        continue;
      }
      if (seenParentIds.has(custodian.id)) {
        continue;
      }
      const section = sectionSelector(state, custodian.sectionId);
      const sheet = section ? sheetSelector(state, section.sheetId) : null;
      if (!section || !sheet || sheet.category !== category.ASSET) {
        continue;
      }
      seenParentIds.add(custodian.id);
      suggestions.push({
        name: custodian.name,
        isNew: false,
        location: {
          isParent: true,
          parentCustodianId: custodian.id,
          parentCustodianName: custodian.name,
          section,
          sheet
        }
      });
    }
    return suggestions;
  }

  render() {
    if (this.state.dataExtracted) {
      const gridOptions = {
        getRowStyle: (row, rowIndex, sectionIndex) => {
          return {
            alignItems: "flex-start",
            paddingTop: "10px",
            paddingBottom: "10px",
            height: "auto",
            backgroundColor: row.cells[0].itemState === itemState.IGNORED ? "#EBEBEB" : "white"
          };
        }
      };

      const rowCountToImport = this.getRowCountToImport();
      const importedKey = this.props.isReviewDataFlow ? "aiImport.syncedRows" : "aiImport.importedRows";
      const countAwareTitle = key => {
        const string = i18n.t(key).replace("%s1%", rowCountToImport);
        return rowCountToImport !== 1 ? string : string.slice(0, -1);
      };
      let importButtonString;
      if (this.state.isImportDone) {
        importButtonString = countAwareTitle(importedKey);
      } else if (this.props.isReviewDataFlow) {
        importButtonString = i18n.t("aiSync.confirmAndSync");
      } else if (rowCountToImport === 0) {
        importButtonString = i18n.t("aiImport.importRowsZero");
      } else {
        importButtonString = countAwareTitle("aiImport.importRows");
      }
      const totalResults =
        this.state.dataExtracted.asset.length + this.state.dataExtracted.debt.length - this.hiddenItemIds.size;

      return (
        <Container>
          <ContentContainer>
            <ActionButtonsContainer>
              <ImportButton
                loaderCss={css`
                  border-color: rgb(229, 229, 229, 0.5);
                  border-top-color: black;
                `}
                isDisabled={this.state.isImportDone || this.state.isImporting || rowCountToImport === 0}
                onClick={this.handleImportButtonClick}
                title={importButtonString}
                isLoading={this.state.isImporting}
              />
              <RescanHelpText>{i18n.t("aiImport.rescanHelp")}</RescanHelpText>
              <RescanButton onClick={this.props.onRescan} title={i18n.t("rescan")} />
            </ActionButtonsContainer>
            <ResultsContainer>
              {this.props.isDataPartial === true && (
                <PartialResultMessage>
                  {i18n.t("aiImport.partialResultMessage").replace("%s1%", Math.floor((totalResults - 8) / 5) * 5)}
                </PartialResultMessage>
              )}
              {this.state.assetGridData && this.state.assetGridData.sheets[0].sections[0].rows.length > 0 && (
                <GridContainer>
                  <Grid
                    gridOptions={gridOptions}
                    gridData={this.state.assetGridData}
                    getEmptyRow={this.getEmptyAssetRow}
                    onChange={this.handleAssetChange.bind(this)}
                    onRowUpdate={this.handleAssetRowUpdate}
                    onCellInvalidTickerAdded={(sheetIndex, sectionIndex, rowIndex, cellIndex) =>
                      this.handleCellInvalidTickerAdded(
                        this.state.assetGridData,
                        sheetIndex,
                        sectionIndex,
                        rowIndex,
                        cellIndex
                      )
                    }
                    onRowContextMenuSelection={this.handleAssetRowContextMenuSelection}
                  />
                </GridContainer>
              )}
              {this.state.debtGridData && this.state.debtGridData.sheets[0].sections[0].rows.length > 0 && (
                <GridContainer margin={"20px 0px 0px 0px"}>
                  <Grid
                    gridOptions={gridOptions}
                    gridData={this.state.debtGridData}
                    getEmptyRow={this.getEmptyDebtRow}
                    onChange={this.handleDebtChange.bind(this)}
                    onCellInvalidTickerAdded={(sheetIndex, sectionIndex, rowIndex, cellIndex) =>
                      this.handleCellInvalidTickerAdded(
                        this.state.debtGridData,
                        sheetIndex,
                        sectionIndex,
                        rowIndex,
                        cellIndex
                      )
                    }
                    onRowContextMenuSelection={this.handleDebtRowContextMenuSelection}
                  />
                </GridContainer>
              )}
            </ResultsContainer>
          </ContentContainer>
          {!this.state.showTickerPickerForAssetRow === false && (
            <TickersPickerDialog
              fetchTickersWithoutRate={true}
              hidePrivateCustodians={true}
              hideTickerPrice={true}
              data={{ props: { allowUnusedTickers: true } }}
              onVariableUpdate={this.handleTickerSelection}
              onDismiss={this.handleTickerPickerDismiss}
            />
          )}
          {!this.state.showLocationPickerForAssetRow === false && (
            <CustodiansPickerDialog
              category={category.ASSET}
              isLocation={true}
              splitView={true}
              subTitle={
                this.state.dataExtracted.asset.find(item => item.id === this.state.showLocationPickerForAssetRow.id)
                  ?.name
              }
              allowCategoryChange={true}
              allowHoldingMode={true}
              isHolding={this.isRowHolding(this.state.showLocationPickerForAssetRow)}
              holdingAccountSuggestions={this.getHoldingAccountSuggestions(this.state.showLocationPickerForAssetRow)}
              showSuggestions={true}
              suggestions={this.getNewPathSuggestions()}
              assetDescription={i18n.t("aiImport.selectAssetDesc")}
              debtDescription={i18n.t("aiImport.selectDebtDesc")}
              filterResults={result =>
                result.isCustodian === true &&
                !result.parentId === true &&
                (!result.linkType === true || result.linkType === accountLinkingService.MANUAL_CONNECTED)
              }
              returnResultWithDetails={true}
              onVariableUpdate={data =>
                this.handleAssetLocationSelection(this.state.showLocationPickerForAssetRow, data.items[0])
              }
              onDismiss={this.handleLocationPickerDismiss}
            />
          )}
          {!this.state.showLocationPickerForDebtRow === false && (
            <CustodiansPickerDialog
              category={category.DEBT}
              isLocation={true}
              splitView={true}
              subTitle={
                this.state.dataExtracted.debt.find(item => item.id === this.state.showLocationPickerForDebtRow.id)?.name
              }
              allowCategoryChange={true}
              showSuggestions={true}
              suggestions={this.getNewPathSuggestions()}
              assetDescription={i18n.t("aiImport.selectAssetDesc")}
              debtDescription={i18n.t("aiImport.selectDebtDesc")}
              filterResults={result => {
                if (result.isCustodian) {
                  return !result.linkType === true || result.linkType === accountLinkingService.MANUAL_CONNECTED;
                } else if (result.isSheet) {
                  return (
                    sheetSectionsSelector(this.context.store.getState(), this.props.currentPortfolio.id, result.id)
                      .length === 1
                  );
                }
                return true;
              }}
              returnResultWithDetails={true}
              onVariableUpdate={data =>
                this.handleDebtLocationSelection(this.state.showLocationPickerForDebtRow, data.items[0])
              }
              onDismiss={this.handleLocationPickerDismiss}
            />
          )}
          <ToolTip
            ref={this.locationToolTipRef}
            size={{ width: 15, height: 12 }}
            arrowOffset={15}
            targetId={this.getLocationToolTipTargetId()}
            align={toolTipAlignment.LEFT}
            positiveButtonTitle={i18n.t("dismiss")}
            onPositiveClick={this.handleLocationToolTipDismiss}
          />
        </Container>
      );
    }
  }
}

const mapStateToProps = (state, props) => ({
  currentPortfolio: currentPortfolioSelector(state),
  defaultTargetSheet: sheetSelector(state, props.targetSheetId),
  userPreferences: userPreferencesSelector(state),
  aiSupportedFileTypes: aiSupportedFileTypesSelector(state)
});

const mapDispatchToProps = {
  uploadAiImportDocument,
  getAiImportExtractedData,
  updateUserPreferences,
  rescanAiImportDocument,
  showToastTip: showToastTip,
  fetchTickerDetails: fetchTickerDetails,
  convertPVSTRateToValueExchangeRate
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(AiAccountImportComponent));
